import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { JobStatus, statusToLabel } from "../../../pages/playground/types";
import { LoadingButton } from "@mui/lab";
import { useState } from "react";

interface BulkActionBarProps {
  selectedJobsCount: number;
  onStatusChange: (status: JobStatus) => void;
  onClear: () => void;
  isLoading?: boolean;
}

// Statuses that managers can set in bulk
const ALLOWED_BULK_STATUSES = [
  JobStatus.IN_PROGRESS,
  JobStatus.TO_BE_REVIEWED,
  JobStatus.TO_BE_REWORKED,
  JobStatus.DELIVERED,
];

export default function BulkActionBar({
  selectedJobsCount,
  onStatusChange,
  onClear,
  isLoading = false,
}: BulkActionBarProps) {
  const [selectedStatus, setSelectedStatus] = useState<JobStatus | "">("");

  const handleStatusChange = (event: SelectChangeEvent) => {
    setSelectedStatus(event.target.value as JobStatus);
  };

  const handleApplyChanges = () => {
    if (selectedStatus) {
      onStatusChange(selectedStatus as JobStatus);
      setSelectedStatus(""); // Clear selection after applying
    }
  };

  // Always show the bar, even with 0 selected

  return (
    <Box
      sx={{
        position: "sticky",
        top: 80, // Account for header height + spacing
        zIndex: 5, // Lower than header
        backgroundColor: "#f8f9fa", // Light gray background
        border: "1px solid #e9ecef", // Subtle border
        color: "#495057", // Dark gray text
        p: 2,
        mb: 2,
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Chip
          label={`${selectedJobsCount} selected`}
          size="small"
          sx={{
            backgroundColor: "#e3f2fd", // Light blue background
            color: "#1976d2", // Blue text
            fontWeight: "medium",
          }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel
            sx={{ 
              color: "#6c757d",
              "&.Mui-focused": {
                color: "#495057"
              }
            }}
            id="bulk-status-select-label"
          >
            Change Status To
          </InputLabel>
          <Select
            labelId="bulk-status-select-label"
            label="Change Status To"
            value={selectedStatus}
            onChange={handleStatusChange}
            sx={{
              backgroundColor: "white",
              color: "#495057",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#dee2e6",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#adb5bd",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#86b7fe",
              },
              "& .MuiSvgIcon-root": {
                color: "#6c757d",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 300,
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  border: "1px solid #dee2e6",
                },
              },
            }}
          >
            {ALLOWED_BULK_STATUSES.map((status) => (
              <MenuItem 
                key={status} 
                value={status}
                sx={{
                  "&:hover": {
                    backgroundColor: "#f8f9fa"
                  }
                }}
              >
                {statusToLabel(status)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <LoadingButton
          variant="contained"
          loading={isLoading}
          disabled={isLoading || !selectedStatus}
          onClick={handleApplyChanges}
          sx={{
            backgroundColor: "#0d6efd", // Professional blue
            color: "white",
            fontWeight: "medium",
            px: 3,
            py: 1,
            borderRadius: 2,
            "&:hover": {
              backgroundColor: "#0b5ed7", // Darker blue on hover
            },
            "&:disabled": {
              backgroundColor: "#6c757d",
              color: "#ffffff",
            },
          }}
        >
          Apply Changes
        </LoadingButton>
      </Box>

      <Button
        variant="text"
        onClick={onClear}
        sx={{
          color: "#6c757d",
          fontWeight: "medium",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
            color: "#495057",
          },
        }}
      >
        Clear Selection
      </Button>
    </Box>
  );
}
